import { useTranslation } from 'react-i18next';

// Components
import { PopoverContent, PopoverTrigger } from '../../atoms/Popover/Popover';
import IconButton from '../../atoms/IconButton/IconButton';
import NavigationLinkGroup from '../../molecules/NavigationLinkGroup/NavigationLinkGroup';

// Utils & misc
import cn from '../../../utils/cn';

// Store
import { Popover } from '../../../store/popover/PopoverContext';

// Style
import style from './NavigationMenuPopover.module.css';

/** Same column count as the `.groups` grid (`--group-columns`) */
const SETTINGS_LINK_COLUMNS = 5;

/**
 * NavigationMenuPopover component - Mega menu of a sidebar sub item (links, groups, configuration zone).
 * @param {object} params
 * @param {boolean} params.isOpen - Whether the popover is open
 * @param {(open: boolean) => void} params.onOpenChange - Open state change (outside click, Escape)
 * @param {string} params.title - Popover title
 * @param {string | null} [params.description=null] - Text displayed at the top right
 * @param {string | null} [params.settingsDescription=null] - Text displayed under the configuration title
 * @param {import('../../../domain/menu/menuNavigationUseCase').MenuPopoverView} params.view - Links and groups
 * @param {(id: string) => void} params.onLinkClick - Link click handler
 * @param {() => void} [params.onManageClick] - Manage button click handler (no button when omitted)
 * @param {number} [params.hiddenCount=0] - Hidden menus, displayed next to the manage button
 * @param {(id: string) => void} [params.onFavoriteToggle] - Link star click handler (no star when omitted)
 * @param {import('@floating-ui/react').Placement} [params.placement='right-start'] - Popover placement
 * @param {number} [params.offsetX=36] - Horizontal offset from the trigger
 * @param {string} [params.triggerClassName] - Additional CSS classes of the trigger wrapper
 * @param {React.ReactNode} params.children - Trigger content
 * @returns {JSX.Element} NavigationMenuPopover component
 */
function NavigationMenuPopover({
  isOpen,
  onOpenChange,
  title,
  description = null,
  settingsDescription = null,
  view,
  onLinkClick,
  onManageClick,
  hiddenCount = 0,
  onFavoriteToggle,
  placement = 'right-start',
  offsetX = 36,
  triggerClassName,
  children,
}) {
  const { t } = useTranslation();
  const hasSettings = view.settingLinks.length > 0 || view.settingGroups.length > 0;

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange} placement={placement} offsetX={offsetX}>
      <PopoverTrigger asChild>
        {/* Wrapper: the trigger needs a DOM ref, children may not forward it */}
        <div className={cn([style.trigger, triggerClassName])}>{children}</div>
      </PopoverTrigger>
      <PopoverContent className={style['menu-popover']}>
        <div className={style.header}>
          <p className={style.title}>{title}</p>
          <div className={style['header-end']}>
            {description ? <p className={style.description}>{description}</p> : null}
            {onManageClick ? (
              <div className={style.manage}>
                {hiddenCount > 0 ? (
                  <p className={style['hidden-count']}>{t('menu.popover.hiddenCount', { count: hiddenCount })}</p>
                ) : null}
                <IconButton
                  icon="setting-2"
                  color="primary"
                  size="medium"
                  title={t('menu.popover.manage')}
                  onClick={onManageClick}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className={style.body}>
          {view.links.length ? (
            <NavigationLinkGroup links={view.links} onLinkClick={onLinkClick} onFavoriteToggle={onFavoriteToggle} />
          ) : null}
          {view.groups.length ? (
            <div className={style.groups}>
              {view.groups.map((group) => (
                <NavigationLinkGroup
                  key={group.id}
                  title={group.title}
                  links={group.links}
                  onLinkClick={onLinkClick}
                  onFavoriteToggle={onFavoriteToggle}
                />
              ))}
            </div>
          ) : null}
        </div>

        {hasSettings ? (
          <div className={style.settings}>
            <p className={style['settings-title']}>{t('menu.popover.configuration')}</p>
            {settingsDescription ? (
              <p className={style['settings-description']}>{settingsDescription}</p>
            ) : null}
            {view.settingGroups.length ? (
              <div className={style.groups}>
                {view.settingLinks.length ? (
                  <NavigationLinkGroup links={view.settingLinks} onLinkClick={onLinkClick} onFavoriteToggle={onFavoriteToggle} />
                ) : null}
                {view.settingGroups.map((group) => (
                  <NavigationLinkGroup
                    key={group.id}
                    title={group.title}
                    links={group.links}
                    onLinkClick={onLinkClick}
                    onFavoriteToggle={onFavoriteToggle}
                  />
                ))}
              </div>
            ) : (
              // No group: spread the links over the same columns as the groups grid
              <NavigationLinkGroup
                links={view.settingLinks}
                columns={SETTINGS_LINK_COLUMNS}
                onLinkClick={onLinkClick}
                onFavoriteToggle={onFavoriteToggle}
              />
            )}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export default NavigationMenuPopover;
